import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Button,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Alert,
    CircularProgress,
    Chip,
    Stack,
    IconButton,
    Tooltip,
    ToggleButtonGroup,
    ToggleButton,
    Autocomplete,
    FormControlLabel,
    Switch,
} from "@mui/material";
import {
    Add as AddIcon,
    ArrowBack as BackIcon,
    DirectionsCar as MilesIcon,
    CheckCircle as ApproveIcon,
    DoDisturbAlt as UnapproveIcon,
    Edit as EditIcon,
    Payments as PaidIcon,
    MoneyOff as UnpayIcon,
    ArrowBackIos as PrevIcon,
    ArrowForwardIos as NextIcon,
    Person as PersonIcon,
    Group as GroupIcon,
} from "@mui/icons-material";
import ExpenseService from "../../services/Expense.service";
import ProjectService from "../../services/Project.service";
import QuoteService from "../../services/Quote.service";
import { usePermissions } from "../../hooks/usePermissions";
import { useAuth } from "../../contexts/AuthContext";

const fmtDate = (dateStr) =>
    new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });

const fmtCurrency = (val) =>
    Number(val).toLocaleString("en-US", { style: "currency", currency: "USD" });

// ── Project / Quote Link Field ────────────────────────────────────────────────

const ProjectQuoteLinkField = ({ linkType, setLinkType, linkValue, setLinkValue }) => {
    const [search, setSearch] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setSearch("");
        setOptions([]);
    }, [linkType]);

    useEffect(() => {
        if (linkType === "none" || search.length < 2) {
            setOptions([]);
            return;
        }
        setLoading(true);
        const service = linkType === "project" ? ProjectService : QuoteService;
        const timer = setTimeout(() => {
            service
                .search(search, { is_archived: false })
                .then((res) => {
                    setOptions(
                        (res.data.results ?? res.data).map((item) => ({
                            id: item.id,
                            label: `${item.number} – ${item.name}`,
                        })),
                    );
                })
                .catch(() => {})
                .finally(() => setLoading(false));
        }, 350);
        return () => clearTimeout(timer);
    }, [search, linkType]);

    return (
        <Stack spacing={1.5}>
            <FormControlLabel
                control={
                    <Switch
                        checked={linkType !== "none"}
                        onChange={(e) => {
                            setLinkValue(null);
                            setLinkType(e.target.checked ? "project" : "none");
                        }}
                    />
                }
                label="Link to Project/Quote"
            />
            {linkType !== "none" && (
                <Stack spacing={1.5}>
                    <ToggleButtonGroup
                        value={linkType}
                        exclusive
                        size="small"
                        onChange={(_, val) => {
                            if (val) {
                                setLinkValue(null);
                                setLinkType(val);
                            }
                        }}
                    >
                        <ToggleButton value="project" sx={{ width: 100 }}>Project</ToggleButton>
                        <ToggleButton value="quote" sx={{ width: 100 }}>Quote</ToggleButton>
                    </ToggleButtonGroup>
                    <Autocomplete
                        fullWidth
                        options={options}
                        value={linkValue}
                        onChange={(_, v) => setLinkValue(v)}
                        inputValue={search}
                        onInputChange={(_, v) => setSearch(v)}
                        loading={loading}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={linkType === "project" ? "Project" : "Quote"}
                                size="small"
                                placeholder="Type to search…"
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                </Stack>
            )}
        </Stack>
    );
};

// ── Add Dialog ─────────────────────────────────────────────────────────────────

const AddDialog = ({ open, onClose, onSaved }) => {
    const [form, setForm] = useState({
        miles: "",
        travel_date: new Date().toISOString().split("T")[0],
        notes: "",
    });
    const [linkType, setLinkType] = useState("none");
    const [linkValue, setLinkValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open) {
            setForm({
                miles: "",
                travel_date: new Date().toISOString().split("T")[0],
                notes: "",
            });
            setLinkType("none");
            setLinkValue(null);
            setError(null);
        }
    }, [open]);

    const set = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (
            !form.miles ||
            isNaN(Number(form.miles)) ||
            Number(form.miles) <= 0
        ) {
            setError("A valid mileage amount is required.");
            return;
        }
        if (!form.travel_date) {
            setError("Travel date is required.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await ExpenseService.createMiles({
                miles: form.miles,
                travel_date: form.travel_date,
                notes: form.notes,
                project: linkType === "project" && linkValue ? linkValue.id : null,
                quote: linkType === "quote" && linkValue ? linkValue.id : null,
            });
            await onSaved();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.entries(detail)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" | ")
                    : detail || "Failed to submit mileage.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
            >
                Log Mileage
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Miles *"
                            type="number"
                            inputProps={{ min: 0, step: "0.1" }}
                            fullWidth
                            value={form.miles}
                            onChange={set("miles")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Travel Date *"
                            type="date"
                            fullWidth
                            value={form.travel_date}
                            onChange={set("travel_date")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ProjectQuoteLinkField
                            linkType={linkType}
                            setLinkType={setLinkType}
                            linkValue={linkValue}
                            setLinkValue={setLinkValue}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            value={form.notes}
                            onChange={set("notes")}
                            inputProps={{ maxLength: 250 }}
                            helperText={`${form.notes.length} / 250`}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    Submit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Edit Dialog ─────────────────────────────────────────────────────────────────

const EditDialog = ({ open, onClose, onSaved, expense }) => {
    const [form, setForm] = useState({ miles: "", travel_date: "", notes: "" });
    const [linkType, setLinkType] = useState("none");
    const [linkValue, setLinkValue] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (open && expense) {
            setForm({
                miles: expense.miles ?? "",
                travel_date: expense.travel_date ?? "",
                notes: expense.notes ?? "",
            });
            if (expense.project) {
                setLinkType("project");
                setLinkValue({ id: expense.project, label: `${expense.project_number} – ${expense.project_name}` });
            } else if (expense.quote) {
                setLinkType("quote");
                setLinkValue({ id: expense.quote, label: `${expense.quote_number} – ${expense.quote_name}` });
            } else {
                setLinkType("none");
                setLinkValue(null);
            }
            setError(null);
        }
    }, [open, expense]);

    const set = (field) => (e) =>
        setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (
            !form.miles ||
            isNaN(Number(form.miles)) ||
            Number(form.miles) <= 0
        ) {
            setError("A valid mileage amount is required.");
            return;
        }
        if (!form.travel_date) {
            setError("Travel date is required.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            await ExpenseService.updateMiles(expense.id, {
                miles: form.miles,
                travel_date: form.travel_date,
                notes: form.notes,
                project: linkType === "project" && linkValue ? linkValue.id : null,
                quote: linkType === "quote" && linkValue ? linkValue.id : null,
            });
            await onSaved();
            onClose();
        } catch (err) {
            const detail = err.response?.data;
            setError(
                typeof detail === "object"
                    ? Object.entries(detail)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(" | ")
                    : detail || "Failed to update mileage.",
            );
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle
                sx={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700 }}
            >
                Edit Mileage Entry
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Miles *"
                            type="number"
                            inputProps={{ min: 0, step: "0.1" }}
                            fullWidth
                            value={form.miles}
                            onChange={set("miles")}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Travel Date *"
                            type="date"
                            fullWidth
                            value={form.travel_date}
                            onChange={set("travel_date")}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <ProjectQuoteLinkField
                            linkType={linkType}
                            setLinkType={setLinkType}
                            linkValue={linkValue}
                            setLinkValue={setLinkValue}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            label="Notes"
                            fullWidth
                            multiline
                            rows={3}
                            value={form.notes}
                            onChange={set("notes")}
                            inputProps={{ maxLength: 250 }}
                            helperText={`${form.notes.length} / 250`}
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ── Expense Item ───────────────────────────────────────────────────────────────

const ExpenseItem = ({
    expense,
    canManage,
    canEdit,
    onApprove,
    onUnapprove,
    onEdit,
    onMarkPaid,
    onUnpay,
}) => (
    <Paper
        elevation={0}
        sx={{
            border: "1px solid",
            borderColor: "primary.main",
            borderRadius: 1,
            p: 2,
        }}
    >
        {/* Content */}
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexWrap: "wrap",
                mb: 0.5,
            }}
        >
            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
                {fmtCurrency(expense.price)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
                {Number(expense.miles).toLocaleString()} mi
            </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
            {expense.travel_date ? fmtDate(expense.travel_date) : ""}
            {expense.user_name ? ` · ${expense.user_name}` : ""}
        </Typography>
        {(expense.project_number || expense.quote_number) && (
            <Typography
                variant="caption"
                color="text.secondary"
                display="block"
            >
                {expense.project_number || expense.quote_number}
                {expense.project_name || expense.quote_name
                    ? ` · ${expense.project_name || expense.quote_name}`
                    : ""}
            </Typography>
        )}
        {expense.notes && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {expense.notes}
            </Typography>
        )}
        {/* Status + actions row */}
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mt: 1.5,
                pt: 1,
                borderTop: "1px solid",
                borderColor: "divider",
            }}
        >
            <Stack direction="row" spacing={0.5}>
                <Chip
                    label={expense.is_approved ? "Approved" : "Pending"}
                    color={expense.is_approved ? "success" : "default"}
                    size="small"
                />
                <Chip
                    label={expense.is_paid ? "Paid" : "Unpaid"}
                    color={expense.is_paid ? "primary" : "default"}
                    size="small"
                />
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center">
                {canEdit && (
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => onEdit(expense)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canManage && !expense.is_approved && (
                    <Tooltip title="Approve">
                        <IconButton
                            size="small"
                            color="success"
                            onClick={() => onApprove(expense)}
                        >
                            <ApproveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canManage && expense.is_approved && (
                    <Tooltip title="Un-approve">
                        <IconButton
                            size="small"
                            color="warning"
                            onClick={() => onUnapprove(expense)}
                        >
                            <UnapproveIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canManage && !expense.is_paid && (
                    <Tooltip title="Mark Paid">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => onMarkPaid(expense)}
                        >
                            <PaidIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {canManage && expense.is_paid && (
                    <Tooltip title="Mark Unpaid">
                        <IconButton
                            size="small"
                            color="warning"
                            onClick={() => onUnpay(expense)}
                        >
                            <UnpayIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    </Paper>
);

// ── Main Page ──────────────────────────────────────────────────────────────────

function Miles() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const { isAdmin, isManagement, isAccounting } = usePermissions();
    const canManage = isAdmin || isManagement || isAccounting;

    const today = new Date();
    const [month, setMonth] = useState(today.getMonth());
    const [year, setYear] = useState(today.getFullYear());
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addOpen, setAddOpen] = useState(false);
    const [editExpense, setEditExpense] = useState(null);
    const [showAll, setShowAll] = useState(false);

    const startDate = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const endDate = `${year}-${String(month + 1).padStart(2, "0")}-${new Date(year, month + 1, 0).getDate()}`;

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await ExpenseService.list(startDate, endDate);
            setExpenses(res.data.miles ?? []);
        } catch {
            setError("Failed to load mileage entries.");
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const prevMonth = () => {
        if (month === 0) {
            setMonth(11);
            setYear((y) => y - 1);
        } else setMonth((m) => m - 1);
    };
    const nextMonth = () => {
        if (month === 11) {
            setMonth(0);
            setYear((y) => y + 1);
        } else setMonth((m) => m + 1);
    };

    const monthLabel = new Date(year, month).toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
    });

    const handleApprove = async (expense) => {
        try {
            await ExpenseService.approve("miles", expense.id, true);
            setExpenses((prev) => prev.map((e) => (e.id === expense.id ? { ...e, is_approved: true } : e)));
        } catch {
            setError("Failed to approve entry.");
        }
    };
    const handleUnapprove = async (expense) => {
        try {
            await ExpenseService.approve("miles", expense.id, false);
            setExpenses((prev) => prev.map((e) => (e.id === expense.id ? { ...e, is_approved: false } : e)));
        } catch {
            setError("Failed to un-approve entry.");
        }
    };
    const handleMarkPaid = async (expense) => {
        try {
            await ExpenseService.markPaid("miles", expense.id, true);
            fetchData();
        } catch {
            setError("Failed to mark entry as paid.");
        }
    };
    const handleUnpay = async (expense) => {
        try {
            await ExpenseService.markPaid("miles", expense.id, false);
            fetchData();
        } catch {
            setError("Failed to mark entry as unpaid.");
        }
    };

    const displayed = showAll
        ? expenses
        : expenses.filter((e) => e.user === currentUser?.id);
    const totalMiles = displayed.reduce(
        (sum, e) => sum + Number(e.miles || 0),
        0,
    );
    const totalValue = displayed.reduce(
        (sum, e) => sum + Number(e.price || 0),
        0,
    );
    const approvedTotal = displayed
        .filter((e) => e.is_approved)
        .reduce((s, e) => s + Number(e.price || 0), 0);
    const pendingApproval = displayed
        .filter((e) => !e.is_approved)
        .reduce((s, e) => s + Number(e.price || 0), 0);
    const balanceDue = displayed
        .filter((e) => e.is_approved && !e.is_paid)
        .reduce((s, e) => s + Number(e.price || 0), 0);

    return (
        <Box sx={{ maxWidth: 900, mx: "auto" }}>
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 1,
                    p: 2.5,
                    mb: 2,
                }}
            >
                {/* Row 1: back + toggle */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                    }}
                >
                    <Tooltip title="Back to Expenses">
                        <IconButton
                            size="small"
                            onClick={() => navigate("/expenses")}
                            sx={{ border: 1, borderColor: "primary.main" }}
                        >
                            <BackIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    {canManage && (
                        <ToggleButtonGroup
                            value={showAll ? "all" : "mine"}
                            exclusive
                            onChange={(_, val) => {
                                if (val) setShowAll(val === "all");
                            }}
                            size="small"
                        >
                            <Tooltip title="My entries">
                                <ToggleButton value="mine">
                                    <PersonIcon fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                            <Tooltip title="All employees">
                                <ToggleButton value="all">
                                    <GroupIcon fontSize="small" />
                                </ToggleButton>
                            </Tooltip>
                        </ToggleButtonGroup>
                    )}
                </Box>

                {/* Row 2: month nav + add */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                        <IconButton size="small" onClick={prevMonth}>
                            <PrevIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ minWidth: 120, textAlign: "center" }}
                        >
                            {monthLabel}
                        </Typography>
                        <IconButton size="small" onClick={nextMonth}>
                            <NextIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setAddOpen(true)}
                    >
                        Add
                    </Button>
                </Box>
            </Paper>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Summary card */}
            <Paper
                elevation={0}
                sx={{
                    border: "1px solid",
                    borderColor: "primary.main",
                    borderRadius: 2,
                    p: 3,
                    mb: 2,
                    textAlign: "center",
                }}
            >
                <Typography
                    variant="h4"
                    fontWeight={800}
                    sx={{ fontFamily: "Montserrat, sans-serif", mb: 0.5 }}
                >
                    {monthLabel}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 0.5 }}
                >
                    Miles · {Number(totalMiles).toLocaleString()} mi
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Mileage Reimbursement
                </Typography>
                <Typography
                    variant="body1"
                    fontWeight={600}
                    sx={{ color: "success.main" }}
                >
                    Approved: {fmtCurrency(approvedTotal)}
                </Typography>
                {pendingApproval > 0 && (
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        color="text.secondary"
                    >
                        Pending Approval: {fmtCurrency(pendingApproval)}
                    </Typography>
                )}
                {balanceDue > 0 && (
                    <Typography
                        variant="body1"
                        fontWeight={600}
                        sx={{ color: "warning.main" }}
                    >
                        Balance Due: {fmtCurrency(balanceDue)}
                    </Typography>
                )}
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                >
                    Total: {fmtCurrency(totalValue)}
                </Typography>
            </Paper>

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : displayed.length === 0 ? (
                <Paper
                    elevation={0}
                    sx={{
                        border: "1px solid",
                        borderColor: "primary.main",
                        borderRadius: 1,
                        p: 6,
                        textAlign: "center",
                    }}
                >
                    <MilesIcon
                        sx={{ fontSize: 48, color: "text.disabled", mb: 1 }}
                    />
                    <Typography color="text.secondary">
                        No mileage entries for {monthLabel}.
                    </Typography>
                </Paper>
            ) : (
                <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                    {displayed.map((e) => (
                        <ExpenseItem
                            key={e.id}
                            expense={e}
                            canManage={canManage}
                            canEdit={
                                canManage ||
                                (currentUser?.id === e.user && !e.is_approved)
                            }
                            onApprove={handleApprove}
                            onUnapprove={handleUnapprove}
                            onEdit={setEditExpense}
                            onMarkPaid={handleMarkPaid}
                            onUnpay={handleUnpay}
                        />
                    ))}
                </Box>
            )}

            <AddDialog
                open={addOpen}
                onClose={() => setAddOpen(false)}
                onSaved={fetchData}
            />
            <EditDialog
                open={!!editExpense}
                onClose={() => setEditExpense(null)}
                onSaved={fetchData}
                expense={editExpense}
            />
        </Box>
    );
}

export default Miles;
